import { Approval } from '@mui/icons-material';
import { Badge, Button, Fade, Grid2, Tab, Tabs } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useInvoiceMutation } from 'api/hooks/billing/invoices';
import { ConfirmationDialog } from 'app/components';
import { Searchfield } from 'app/components/Searchfield';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';
import useQueryStringState from 'utils/hooks/useQueryStringState';
import { useTableQuery } from 'utils/hooks/useTableQuery';

import { CSVInvoicesTab } from './components/CSVTab';
import { ExportInvoiceTab } from './components/ExportTab';
import { FinalInvoiceTab } from './components/FinalTab';

enum TabPanels {
  APPROVED = 'APPROVED',
  FINAL = 'FINAL',
  CSV = 'CSV'
}

export const ExportInvoices = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { search, handleSearchTextChange, debouncedSearchText } = useTableQuery();

  usePageHeading(t(translations.pages.billingFinance.exportTitle()));
  const [tab, setTab] = useQueryStringState<TabPanels>('tab', TabPanels.APPROVED);
  const [selectedInvoices, setSelectedInvoices] = useState<GridRowSelectionModel>([]);
  const [exportConfirmationDialogOpen, setExportConfirmationDialogOpen] = useState(false);

  const { updateState } = useInvoiceMutation();

  const handleSelectionChange = (newSelectedInvoices: GridRowSelectionModel) => {
    setSelectedInvoices(newSelectedInvoices);
  };

  const handleExportInvoices = async () => {
    try {
      if (selectedInvoices.length > 0) {
        await updateState.mutateAsync({
          invoiceIds: selectedInvoices.map(id => id.toString()),
          state: 'FINAL'
        });
      }
      dispatch(
        AlertActions.success(t(translations.pages.billingFinance.mutation.exportSuccessful()))
      );
    } catch {
      dispatch(AlertActions.error(t(translations.pages.billingFinance.mutation.exportFailed())));
    } finally {
      setSelectedInvoices([]);
    }
  };

  const renderTabs = () => {
    switch (tab) {
      case TabPanels.APPROVED:
        return (
          <ExportInvoiceTab
            selectedInvoices={selectedInvoices}
            handleSelectionChange={handleSelectionChange}
            search={debouncedSearchText}
          />
        );
      case TabPanels.FINAL:
        return <FinalInvoiceTab search={debouncedSearchText} />;
      case TabPanels.CSV:
        return <CSVInvoicesTab search={debouncedSearchText} />;
      default:
        break;
    }
  };

  const handleTabChange = useCallback(
    (_event, value) => {
      setTab(value);
    },
    [setTab]
  );

  return (
    <>
      <ConfirmationDialog
        open={exportConfirmationDialogOpen}
        title={t(translations.pages.billingFinance.confirmationDialog.exportTitle())}
        onClose={() => {
          setExportConfirmationDialogOpen(false);
        }}
        onConfirm={() => {
          handleExportInvoices();
          setExportConfirmationDialogOpen(false);
        }}
      >
        {t(translations.pages.billingFinance.confirmationDialog.exportDialog())}
      </ConfirmationDialog>
      <Grid2 container justifyContent={'space-between'} alignItems={'center'}>
        <Grid2 container gap={1}>
          <Tabs value={tab} onChange={handleTabChange} indicatorColor="primary">
            {Object.keys(TabPanels).map(tab => (
              <Tab
                value={tab}
                key={tab}
                label={t(translations.pages.billingFinance.tabs[tab.toLowerCase()]())}
              />
            ))}
          </Tabs>
          <Searchfield handleSearch={handleSearchTextChange} search={search} />
        </Grid2>
        <Fade in={tab === TabPanels.APPROVED}>
          <Badge badgeContent={selectedInvoices.length} color="secondary">
            <Button
              variant="contained"
              startIcon={<Approval />}
              style={{ maxHeight: '2.6em' }}
              disabled={selectedInvoices.length === 0}
              onClick={() => setExportConfirmationDialogOpen(true)}
            >
              {t(translations.pages.billingFinance.exportButton())}
            </Button>
          </Badge>
        </Fade>
      </Grid2>
      <Grid2 container>{renderTabs()}</Grid2>
    </>
  );
};
